/**
 * WorkspaceInteractions - Shared visual interactive sub-task blocks.
 *
 * The backend randomly mixes 1-3 interaction types into a workspace task
 * (task.interactions). These generic blocks render them:
 * - drag_drop:      drag palette items into drop zones (@dnd-kit)
 * - wire_connect:   connect left pins to right pins (click-click, SVG wires)
 * - fill_blank:     labeled value inputs
 * - order_sequence: sortable list (@dnd-kit/sortable)
 * - toggle_state:   switches / sliders / numeric inputs
 * - select_mark:    mark items with a severity level + comment
 *
 * useWorkspaceInteractions(task) returns { extras, collect } — extras is the
 * rendered node, collect() returns the submission payload for all blocks.
 */

import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, MousePointerClick, Cable, ToggleRight, ListOrdered, Highlighter, PencilLine, X } from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

interface PaletteItem { id: string; label: string; hint?: string }
interface DropZone { id: string; label: string; capacity?: number }
interface Pin { id: string; label: string; hint?: string }
interface Blank { id: string; label: string; unit?: string; hint?: string }
interface OrderItem { id: string; label: string }
interface Control { id: string; label: string; type: 'switch' | 'slider' | 'input'; value: string; min?: number; max?: number; unit?: string }
interface MarkTarget { id: string; label: string; text: string }

type ResultsRef = React.MutableRefObject<Record<string, any>>;

const BlockCard: React.FC<{ icon: React.ReactNode; title: string; instruction?: string; children: React.ReactNode }> = ({
  icon, title, instruction, children,
}) => (
  <div className="card space-y-3">
    <div className="flex items-start gap-2">
      <div className="flex-shrink-0 mt-0.5 text-primary-600">{icon}</div>
      <div>
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        {instruction && <p className="text-xs text-gray-600 mt-0.5">{instruction}</p>}
      </div>
    </div>
    {children}
  </div>
);

// ============================================================================
// 1) Drag & Drop
// ============================================================================

const DraggableItem: React.FC<{ item: PaletteItem; placed?: boolean }> = ({ item, placed }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: item.id });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{ transform: CSS.Translate.toString(transform), touchAction: 'none' }}
      className={`px-3 py-2 rounded-lg border text-xs font-medium cursor-grab active:cursor-grabbing select-none shadow-sm ${
        isDragging ? 'opacity-60 border-primary-400 bg-primary-50 z-50' : placed ? 'border-gray-200 bg-gray-100 text-gray-400' : 'border-gray-300 bg-white text-gray-800 hover:border-primary-400'
      }`}
    >
      <span className="flex items-center gap-1.5">
        <GripVertical size={12} className="text-gray-400 flex-shrink-0" />
        {item.label}
      </span>
      {item.hint && <span className="block text-[10px] text-gray-400 mt-0.5 ml-4">{item.hint}</span>}
    </div>
  );
};

const DroppableZone: React.FC<{
  zone: DropZone;
  items: PaletteItem[];
  onRemove: (itemId: string) => void;
}> = ({ zone, items, onRemove }) => {
  const { setNodeRef, isOver } = useDroppable({ id: zone.id });
  const full = zone.capacity != null && items.length >= zone.capacity;
  return (
    <div
      ref={setNodeRef}
      className={`rounded-lg border-2 border-dashed p-2 min-h-[72px] transition-colors ${
        isOver ? 'border-primary-500 bg-primary-50' : full ? 'border-gray-200 bg-gray-50' : 'border-gray-300 bg-gray-50/50'
      }`}
    >
      <div className="text-[11px] font-semibold text-gray-700 mb-1.5 flex items-center justify-between">
        <span>{zone.label}</span>
        {zone.capacity != null && <span className="text-gray-400 font-normal">{items.length}/{zone.capacity}</span>}
      </div>
      <div className="space-y-1">
        {items.map((it) => (
          <div key={it.id} className="flex items-center gap-1.5 px-2 py-1 rounded bg-white border border-primary-200 text-xs text-gray-800 shadow-sm">
            <span className="flex-1 truncate">{it.label}</span>
            <button onClick={() => onRemove(it.id)} className="text-gray-400 hover:text-red-500 flex-shrink-0">
              <X size={12} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const DragDropBlock: React.FC<{ data: any; resultsRef: ResultsRef }> = ({ data, resultsRef }) => {
  const { t } = useTranslation();
  const palette: PaletteItem[] = Array.isArray(data.palette) ? data.palette : [];
  const zones: DropZone[] = Array.isArray(data.drop_zones) ? data.drop_zones : [];
  const [placements, setPlacements] = useState<Record<string, string>>({});
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } })
  );

  const sync = (next: Record<string, string>) => {
    setPlacements(next);
    resultsRef.current.drag_drop = {
      placements: Object.entries(next).map(([item_id, zone_id]) => ({ item_id, zone_id })),
    };
  };

  const handleDragEnd = (e: DragEndEvent) => {
    const itemId = String(e.active.id);
    const zoneId = e.over ? String(e.over.id) : null;
    const next = { ...placements };
    if (zoneId) {
      const zone = zones.find((z) => z.id === zoneId);
      const count = Object.values(next).filter((z) => z === zoneId && true).length;
      if (zone?.capacity != null && next[itemId] !== zoneId && count >= zone.capacity) return;
      next[itemId] = zoneId;
    } else {
      delete next[itemId];
    }
    sync(next);
  };

  const unplaced = palette.filter((p) => !placements[p.id]);

  return (
    <BlockCard
      icon={<MousePointerClick size={16} />}
      title={t('workspace.interactions.dragDrop')}
      instruction={data.instruction}
    >
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">
              {t('workspace.interactions.palette')}
            </div>
            {unplaced.map((item) => (
              <DraggableItem key={item.id} item={item} />
            ))}
            {unplaced.length === 0 && (
              <div className="text-[11px] text-gray-400 italic">{t('workspace.interactions.allPlaced')}</div>
            )}
          </div>
          <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {zones.map((zone) => (
              <DroppableZone
                key={zone.id}
                zone={zone}
                items={palette.filter((p) => placements[p.id] === zone.id)}
                onRemove={(itemId) => {
                  const next = { ...placements };
                  delete next[itemId];
                  sync(next);
                }}
              />
            ))}
          </div>
        </div>
      </DndContext>
    </BlockCard>
  );
};

// ============================================================================
// 2) Wire Connect (click a left pin, then a right pin)
// ============================================================================

const WireConnectBlock: React.FC<{ data: any; resultsRef: ResultsRef }> = ({ data, resultsRef }) => {
  const { t } = useTranslation();
  const leftPins: Pin[] = Array.isArray(data.left_pins) ? data.left_pins : [];
  const rightPins: Pin[] = Array.isArray(data.right_pins) ? data.right_pins : [];
  const [wires, setWires] = useState<{ from: string; to: string }[]>([]);
  const [pendingLeft, setPendingLeft] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const pinRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [lines, setLines] = useState<{ x1: number; y1: number; x2: number; y2: number }[]>([]);

  const sync = (next: { from: string; to: string }[]) => {
    setWires(next);
    resultsRef.current.wire_connect = { wires: next };
  };

  const recalcLines = useCallback(() => {
    const cont = containerRef.current;
    if (!cont) return;
    const cRect = cont.getBoundingClientRect();
    setLines(
      wires
        .map((w) => {
          const a = pinRefs.current[`L:${w.from}`]?.getBoundingClientRect();
          const b = pinRefs.current[`R:${w.to}`]?.getBoundingClientRect();
          if (!a || !b) return null;
          return {
            x1: a.right - cRect.left,
            y1: a.top + a.height / 2 - cRect.top,
            x2: b.left - cRect.left,
            y2: b.top + b.height / 2 - cRect.top,
          };
        })
        .filter(Boolean) as any
    );
  }, [wires]);

  useLayoutEffect(() => {
    recalcLines();
    window.addEventListener('resize', recalcLines);
    return () => window.removeEventListener('resize', recalcLines);
  }, [recalcLines]);

  const clickLeft = (id: string) => setPendingLeft(pendingLeft === id ? null : id);
  const clickRight = (id: string) => {
    if (!pendingLeft) return;
    const next = wires.filter((w) => !(w.from === pendingLeft && w.to === id));
    if (next.length === wires.length) next.push({ from: pendingLeft, to: id });
    sync(next);
    setPendingLeft(null);
  };

  const pinBtn = (pin: Pin, side: 'L' | 'R', connected: boolean, active: boolean, onClick: () => void) => (
    <button
      key={pin.id}
      ref={(el) => { pinRefs.current[`${side}:${pin.id}`] = el; }}
      onClick={onClick}
      className={`w-full text-left px-3 py-2 rounded-lg border text-xs transition-colors ${
        active
          ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
          : connected
          ? 'border-emerald-400 bg-emerald-50 text-emerald-900'
          : 'border-gray-300 bg-white hover:border-primary-400'
      }`}
    >
      <span className="font-medium text-gray-900">{pin.label}</span>
      {pin.hint && <span className="block text-[10px] text-gray-400 mt-0.5">{pin.hint}</span>}
    </button>
  );

  return (
    <BlockCard
      icon={<Cable size={16} />}
      title={t('workspace.interactions.wireConnect')}
      instruction={data.instruction}
    >
      <p className="text-[11px] text-gray-500">
        {t('workspace.interactions.wireHelp')}
      </p>
      <div ref={containerRef} className="relative grid grid-cols-2 gap-x-16 gap-y-1.5">
        <svg className="absolute inset-0 w-full h-full pointer-events-none z-10">
          {lines.map((l, i) => (
            <path
              key={i}
              d={`M ${l.x1} ${l.y1} C ${l.x1 + 40} ${l.y1}, ${l.x2 - 40} ${l.y2}, ${l.x2} ${l.y2}`}
              stroke="#10b981"
              strokeWidth="2"
              fill="none"
            />
          ))}
        </svg>
        <div className="space-y-1.5">
          {leftPins.map((p) =>
            pinBtn(p, 'L', wires.some((w) => w.from === p.id), pendingLeft === p.id, () => clickLeft(p.id))
          )}
        </div>
        <div className="space-y-1.5">
          {rightPins.map((p) =>
            pinBtn(p, 'R', wires.some((w) => w.to === p.id), false, () => clickRight(p.id))
          )}
        </div>
      </div>
      {wires.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {wires.map((w, i) => {
            const from = leftPins.find((p) => p.id === w.from)?.label || w.from;
            const to = rightPins.find((p) => p.id === w.to)?.label || w.to;
            return (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] text-emerald-800">
                {from} → {to}
                <button onClick={() => sync(wires.filter((_, j) => j !== i))} className="text-emerald-400 hover:text-red-500">
                  <X size={10} />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </BlockCard>
  );
};

// ============================================================================
// 3) Fill in the blanks
// ============================================================================

const FillBlankBlock: React.FC<{ data: any; resultsRef: ResultsRef }> = ({ data, resultsRef }) => {
  const { t } = useTranslation();
  const blanks: Blank[] = Array.isArray(data.blanks) ? data.blanks : [];
  const [values, setValues] = useState<Record<string, string>>({});

  const sync = (next: Record<string, string>) => {
    setValues(next);
    resultsRef.current.fill_blank = {
      blanks: blanks.map((b) => ({ id: b.id, value: next[b.id] || '' })),
    };
  };

  return (
    <BlockCard
      icon={<PencilLine size={16} />}
      title={t('workspace.interactions.fillBlank')}
      instruction={data.instruction}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {blanks.map((b) => (
          <div key={b.id}>
            <label className="text-xs font-medium text-gray-700">{b.label}</label>
            <div className="flex items-center gap-1.5 mt-1">
              <input
                value={values[b.id] || ''}
                onChange={(e) => sync({ ...values, [b.id]: e.target.value })}
                className="input text-sm flex-1"
                placeholder={b.hint || ''}
              />
              {b.unit && <span className="text-xs text-gray-500 flex-shrink-0">{b.unit}</span>}
            </div>
          </div>
        ))}
      </div>
    </BlockCard>
  );
};

// ============================================================================
// 4) Order sequence (sortable)
// ============================================================================

const SortableRow: React.FC<{ id: string; index: number; label: string }> = ({ id, index, label }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, touchAction: 'none' }}
      {...attributes}
      {...listeners}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border bg-white text-xs cursor-grab active:cursor-grabbing select-none ${
        isDragging ? 'border-primary-400 shadow-lg z-50 opacity-80' : 'border-gray-300'
      }`}
    >
      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-800 text-white text-[10px] flex items-center justify-center">
        {index + 1}
      </span>
      <span className="flex-1 text-gray-800">{label}</span>
      <GripVertical size={14} className="text-gray-400 flex-shrink-0" />
    </div>
  );
};

const OrderSequenceBlock: React.FC<{ data: any; resultsRef: ResultsRef }> = ({ data, resultsRef }) => {
  const { t } = useTranslation();
  const initial: OrderItem[] = Array.isArray(data.items) ? data.items : [];
  const [items, setItems] = useState<OrderItem[]>(initial);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } })
  );

  // Report initial order too, so an untouched list is still submitted
  resultsRef.current.order_sequence = { order: items.map((i) => i.id) };

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setItems((prev) => {
      const oldIdx = prev.findIndex((i) => i.id === active.id);
      const newIdx = prev.findIndex((i) => i.id === over.id);
      const next = arrayMove(prev, oldIdx, newIdx);
      resultsRef.current.order_sequence = { order: next.map((i) => i.id) };
      return next;
    });
  };

  return (
    <BlockCard
      icon={<ListOrdered size={16} />}
      title={t('workspace.interactions.orderSequence')}
      instruction={data.instruction}
    >
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-1.5">
            {items.map((item, idx) => (
              <SortableRow key={item.id} id={item.id} index={idx} label={item.label} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </BlockCard>
  );
};

// ============================================================================
// 5) Toggle state (switches / sliders / inputs)
// ============================================================================

const ToggleStateBlock: React.FC<{ data: any; resultsRef: ResultsRef }> = ({ data, resultsRef }) => {
  const { t } = useTranslation();
  const controls: Control[] = Array.isArray(data.controls) ? data.controls : [];
  const [values, setValues] = useState<Record<string, string>>(
    Object.fromEntries(controls.map((c) => [c.id, String(c.value ?? '')]))
  );

  // Report initial state so untouched controls are still submitted
  resultsRef.current.toggle_state = {
    controls: controls.map((c) => ({ id: c.id, value: values[c.id] ?? String(c.value ?? '') })),
  };

  const sync = (id: string, value: string) => {
    const next = { ...values, [id]: value };
    setValues(next);
    resultsRef.current.toggle_state = {
      controls: controls.map((c) => ({ id: c.id, value: next[c.id] ?? '' })),
    };
  };

  return (
    <BlockCard
      icon={<ToggleRight size={16} />}
      title={t('workspace.interactions.toggleState')}
      instruction={data.instruction}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {controls.map((c) => {
          const v = values[c.id] ?? '';
          if (c.type === 'switch') {
            const on = v === 'on' || v === 'true' || v === '1';
            return (
              <div key={c.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-gray-300 bg-white">
                <span className="text-xs font-medium text-gray-800">{c.label}</span>
                <button
                  onClick={() => sync(c.id, on ? 'off' : 'on')}
                  className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${on ? 'bg-emerald-500' : 'bg-gray-300'}`}
                >
                  <span
                    className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${on ? 'left-5' : 'left-0.5'}`}
                  />
                </button>
              </div>
            );
          }
          if (c.type === 'slider') {
            return (
              <div key={c.id} className="px-3 py-2 rounded-lg border border-gray-300 bg-white">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-medium text-gray-800">{c.label}</span>
                  <span className="font-mono text-gray-600">{v}{c.unit ? ` ${c.unit}` : ''}</span>
                </div>
                <input
                  type="range"
                  min={c.min ?? 0}
                  max={c.max ?? 100}
                  value={Number(v) || 0}
                  onChange={(e) => sync(c.id, e.target.value)}
                  className="w-full accent-primary-600"
                />
              </div>
            );
          }
          return (
            <div key={c.id} className="px-3 py-2 rounded-lg border border-gray-300 bg-white">
              <label className="text-xs font-medium text-gray-800">{c.label}</label>
              <div className="flex items-center gap-1.5 mt-1">
                <input value={v} onChange={(e) => sync(c.id, e.target.value)} className="input text-sm flex-1 py-1" />
                {c.unit && <span className="text-xs text-gray-500">{c.unit}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </BlockCard>
  );
};

// ============================================================================
// 6) Select & mark
// ============================================================================

const SelectMarkBlock: React.FC<{ data: any; resultsRef: ResultsRef }> = ({ data, resultsRef }) => {
  const { t } = useTranslation();
  const targets: MarkTarget[] = Array.isArray(data.targets) ? data.targets : [];
  const options: string[] = Array.isArray(data.mark_options) && data.mark_options.length > 0
    ? data.mark_options
    : ['kritik', 'riskli', 'uygun'];
  const [marks, setMarks] = useState<Record<string, { mark: string; comment: string }>>({});

  const palette = ['bg-red-100 border-red-300 text-red-800', 'bg-amber-100 border-amber-300 text-amber-800', 'bg-emerald-100 border-emerald-300 text-emerald-800', 'bg-blue-100 border-blue-300 text-blue-800'];

  const sync = (next: Record<string, { mark: string; comment: string }>) => {
    setMarks(next);
    resultsRef.current.select_mark = {
      marks: Object.entries(next).map(([target_id, m]) => ({ target_id, mark: m.mark, comment: m.comment })),
    };
  };

  return (
    <BlockCard
      icon={<Highlighter size={16} />}
      title={t('workspace.interactions.selectMark')}
      instruction={data.instruction}
    >
      <div className="space-y-2">
        {targets.map((tg) => {
          const mark = marks[tg.id];
          return (
            <div key={tg.id} className={`rounded-lg border p-3 transition-colors ${mark ? 'border-gray-400 bg-gray-50' : 'border-gray-200 bg-white'}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-gray-900">{tg.label}</div>
                  <p className="text-xs text-gray-600 mt-0.5 whitespace-pre-wrap">{tg.text}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {options.map((opt, oi) => (
                    <button
                      key={opt}
                      onClick={() => {
                        const next = { ...marks };
                        if (mark?.mark === opt) delete next[tg.id];
                        else next[tg.id] = { mark: opt, comment: mark?.comment || '' };
                        sync(next);
                      }}
                      className={`px-2 py-0.5 rounded-full border text-[10px] font-medium transition-all ${
                        mark?.mark === opt ? palette[oi % palette.length] + ' ring-1 ring-offset-1 ring-gray-400' : 'border-gray-200 bg-gray-50 text-gray-500 hover:border-gray-400'
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
              {mark && (
                <textarea
                  value={mark.comment}
                  onChange={(e) => sync({ ...marks, [tg.id]: { ...mark, comment: e.target.value } })}
                  rows={2}
                  className="input text-xs w-full mt-2"
                  placeholder={t('workspace.interactions.markCommentPlaceholder') as string}
                />
              )}
            </div>
          );
        })}
      </div>
    </BlockCard>
  );
};

// ============================================================================
// Hook: renders present interaction blocks + collects their results
// ============================================================================

export function useWorkspaceInteractions(task: any): {
  extras: React.ReactNode;
  collect: () => Record<string, any> | null;
} {
  const resultsRef = useRef<Record<string, any>>({});
  const interactions = task?.interactions && typeof task.interactions === 'object' ? task.interactions : null;

  const collect = useCallback(() => {
    if (!interactions) return null;
    return { ...resultsRef.current };
  }, [interactions]);

  if (!interactions) return { extras: null, collect };

  const blocks: React.ReactNode[] = [];
  if (interactions.drag_drop) blocks.push(<DragDropBlock key="dd" data={interactions.drag_drop} resultsRef={resultsRef} />);
  if (interactions.wire_connect) blocks.push(<WireConnectBlock key="wc" data={interactions.wire_connect} resultsRef={resultsRef} />);
  if (interactions.toggle_state) blocks.push(<ToggleStateBlock key="ts" data={interactions.toggle_state} resultsRef={resultsRef} />);
  if (interactions.order_sequence) blocks.push(<OrderSequenceBlock key="os" data={interactions.order_sequence} resultsRef={resultsRef} />);
  if (interactions.select_mark) blocks.push(<SelectMarkBlock key="sm" data={interactions.select_mark} resultsRef={resultsRef} />);
  if (interactions.fill_blank) blocks.push(<FillBlankBlock key="fb" data={interactions.fill_blank} resultsRef={resultsRef} />);

  return { extras: blocks.length > 0 ? <>{blocks}</> : null, collect };
}

export default useWorkspaceInteractions;
